import { useState, useEffect, memo, useCallback, useRef } from "react";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import AnimacionCarga from "../animacion_de_carga";
import TablaGenerica from "../tablas";
import { obtenerCenso } from "../../../functions/consultas";
import { actualizarRegistro } from "../../../functions/crud";
import BotonSOS from "../sos/boton_sos";
import { crearCopropietario, editarCopropietario, eliminarCopropietario } from "./censo_basico";
import {
  columnas,
  extraerDatosNumerados,
  calcularCoeficienteTotal,
  CONFIGS,
  generarFormularioHTML,
  generarTablaConAcciones
} from './gestionDatosUtils';
import { useImportacionDatos } from './importacionDatos';
import "bootstrap-icons/font/bootstrap-icons.css";


const enviarPlacaAPI = async (apartamento, placa) => {
  try {
    const res = await fetch("https://coordiph.herokuapp.com/api/correo_placas", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "C00rd1nar!"
      },
      body: JSON.stringify({
        apartamento,
        placa,
        nit: localStorage.getItem("CodigoCliente")
      })
    });
    return await res.json();
  } catch (error) {
    console.error("Error enviando datos a la API:", error);
    return null;
  }
};


const CONFIGURACIONES_CAMPOS = {
  residentes: {
    campoBase: 'nombre_residente',
    campos: [
      { local: 'nombre_residente', firebase: 'nombre_residente' },
      { local: 'cedula_residente', firebase: 'cedula_residente' },
      { local: 'telefono_residente', firebase: 'telefono_residente' },
      { local: 'tipo_residente', firebase: 'tipo_residente' },
      { local: 'correo_residente', firebase: 'correo_residente' },
      { local: 'genero_residente', firebase: 'genero_residente' }
    ]
  },
  vehiculos: {
    campoBase: 'placa',
    campos: [
      { local: 'placa', firebase: 'placa' },
      { local: 'marca_carro', firebase: 'marca_carro' },
      { local: 'color_carro', firebase: 'color_carro' },
      { local: 'tipo_vehiculo', firebase: 'tipo_vehiculo' },
      { local: 'parqueadero', firebase: 'parqueadero' }
    ]
  },
  mascotas: {
    campoBase: 'nombre_mascota',
    campos: [
      { local: 'nombre_mascota', firebase: 'nombre_mascota' },
      { local: 'tipo_mascota', firebase: 'tipo_mascota' },
      { local: 'raza_mascota', firebase: 'raza_mascota' },
      { local: 'color_mascota', firebase: 'color_mascota' }
    ]
  },
  autorizados: {
    campoBase: 'nombre_autorizado',
    campos: [
      { local: 'nombre_autorizado', firebase: 'nombre_autorizado' },
      { local: 'cedula_autorizado', firebase: 'cedula_autorizado' },
      { local: 'tipo_relacion_autorizado', firebase: 'tipo_relacion_autorizado' }
    ]
  },
  contactos_emergencia: {
    campoBase: 'nombre_contacto_emergencia',
    campos: [
      { local: 'nombre_contacto_emergencia', firebase: 'nombre_contacto_emergencia' },
      { local: 'telefono_contacto_emergencia', firebase: 'telefono_contacto_emergencia' },
      { local: 'descripcion_contacto_emergencia', firebase: 'descripcion_contacto_emergencia' }
    ]
  },
  espacios: {
    campoBase: 'espacio_tipo',
    campos: [
      { local: 'espacio_tipo', firebase: 'espacio_tipo' },
      { local: 'espacio_identificador', firebase: 'espacio_identificador' },
      { local: 'espacio_coeficiente', firebase: 'espacio_coeficiente' }
    ]
  }
};


const obtenerSiguienteNumero = (vivienda, campoBase) => {
  if (!vivienda || Object.keys(vivienda).length === 0) {
    return 1;
  }

  const indices = [];
  
  for (const key in vivienda) {
    if (key.startsWith(campoBase + '_')) {
      const numeroStr = key.substring((campoBase + '_').length);
      const numero = parseInt(numeroStr);
      
      if (!isNaN(numero) && numero > 0) {
        indices.push(numero);
      }
    }
  }

  if (indices.length === 0) {
    return 1;
  }

  return Math.max(...indices) + 1;
};


const guardarRegistroEnFirebase = async (tipo, valores, ruta, numeroIndex) => {
  const config = CONFIGURACIONES_CAMPOS[tipo];
  
  if (!config) {
    throw new Error(`Configuración no encontrada para tipo: ${tipo}`);
  }

  const promesas = config.campos.map(campo => {
    const valorLocal = valores[campo.local];
    const campoFirebase = `${campo.firebase}_${numeroIndex}`;
    let valor = valorLocal || "";
    
    if (campo.local === 'placa') {
      valor = valor.toUpperCase();
    }
    
    return actualizarRegistro(`${ruta}/${campoFirebase}`, valor);
  });
  
  await Promise.all(promesas);
};


const limpiarRegistroEnFirebase = async (tipo, ruta, numeroIndex) => {
  const config = CONFIGURACIONES_CAMPOS[tipo];
  
  if (!config) {
    throw new Error(`Configuración no encontrada para tipo: ${tipo}`);
  }

  const promesas = config.campos.map(campo => {
    const campoFirebase = `${campo.firebase}_${numeroIndex}`;
    return actualizarRegistro(`${ruta}/${campoFirebase}`, "");
  });
  
  await Promise.all(promesas);
};


const calcularContadores = (fila) => {
  if (!fila) {
    return {
      copropietarios: 0,
      vehiculos: 0,
      mascotas: 0,
      autorizados: 0,
      espacios: 0,
      contactosEmergencia: 0,
      coeficienteTotal: 0
    };
  }

  return {
    copropietarios: extraerDatosNumerados(fila, ['nombre_residente']).length,
    vehiculos: extraerDatosNumerados(fila, ['placa']).length,
    mascotas: extraerDatosNumerados(fila, ['nombre_mascota']).length,
    autorizados: extraerDatosNumerados(fila, ['nombre_autorizado']).length,
    espacios: extraerDatosNumerados(fila, ['espacio_tipo']).length,
    contactosEmergencia: extraerDatosNumerados(fila, ['nombre_contacto_emergencia']).length,
    coeficienteTotal: calcularCoeficienteTotal(fila)
  };
};


const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const ListadoCenso = memo(() => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  
  const datosRef = useRef(datos);
  
  useEffect(() => {
    datosRef.current = datos;
  }, [datos]);

  
  const capturarDatos = useCallback(async () => {
    try {
      setCargando(true);
      const data = await obtenerCenso();
      
      if (data) {
        const datosProcesados = Object.values(data).map(fila => ({
          ...fila,
          ...calcularContadores(fila)
        }));
        setDatos(datosProcesados);
        datosRef.current = datosProcesados;
      } else {
        setDatos([]);
        datosRef.current = [];
      }
    } catch (error) {
      console.error("Error capturando datos:", error);
      setDatos([]);
      datosRef.current = [];
    } finally {
      setCargando(false);
    }
  }, []);

  
  const actualizarFilaEspecifica = useCallback(async (filaId, maxReintentos = 3) => {
    for (let intento = 0; intento < maxReintentos; intento++) {
      try {
        if (intento > 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        const datosCompletos = await obtenerCenso();
        
        if (datosCompletos && datosCompletos[filaId]) {
          const filaActualizada = datosCompletos[filaId];
          const filaConContadores = {
            ...filaActualizada,
            ...calcularContadores(filaActualizada)
          };

          setDatos(prevDatos => {
            const nuevosDatos = prevDatos.map(item => 
              item.id === filaId ? filaConContadores : item
            );
            datosRef.current = nuevosDatos;
            return nuevosDatos;
          });

          return filaConContadores;
        }
      } catch (error) {
        console.error(`Intento ${intento + 1} fallido:`, error);
      }
    }
    console.warn('Usando datos locales como fallback');
    const filaLocal = datosRef.current.find(item => item.id === filaId);
    return filaLocal || null;
  }, []);

  
  const useGestionDatosIntegrado = useCallback((tipo) => {
    return async (fila) => {
      const config = CONFIGS[tipo];
      const configCampos = CONFIGURACIONES_CAMPOS[tipo];
      
      if (!config || !configCampos) {
        Swal.fire("Error", "Configuración no encontrada", "error");
        return;
      }
      let datosModales = extraerDatosNumerados(fila, config.campos);
      let filaActual = { ...fila };
      let modalAbierto = true;

      const actualizarTablaModal = (datosFrescos) => {
        const contenedor = document.getElementById('contenedorTabla');
        if (contenedor && modalAbierto) {
          contenedor.innerHTML = generarTablaConAcciones(datosFrescos, config);
        }
      };

      const obtenerValoresFormulario = () => {
        const valores = {};
        config.campos.forEach(campo => {
          const elemento = document.getElementById(campo);
          if (elemento) {
            valores[campo] = elemento.value.trim();
            if (campo === 'placa') {
              valores[campo] = valores[campo].toUpperCase();
            }
          }
        });
        return valores;
      };

      const limpiarFormulario = () => {
        config.campos.forEach(campo => {
          const elemento = document.getElementById(campo);
          if (elemento) elemento.value = '';
        });
      };

      const validarDatos = (datos) => {
        for (const [campo, valor] of Object.entries(datos)) {
          if (config.validaciones && config.validaciones[campo]) {
            const error = config.validaciones[campo](valor);
            if (error) return error;
          }
        }
        return null;
      };

      
      const sincronizarTodo = async () => {
        try {
          const filaFresca = await actualizarFilaEspecifica(filaActual.id);
          
          if (filaFresca) {
            filaActual = filaFresca;
            datosModales = extraerDatosNumerados(filaFresca, config.campos);
            
            if (modalAbierto) {
              actualizarTablaModal(datosModales);
            }
            
            return filaFresca;
          }
          
          return null;
        } catch (error) {
          console.error("Error en sincronizarTodo:", error);
          return null;
        }
      };

      const editarItem = async (index) => {
        const item = datosModales[index];

        const { value: editValues } = await Swal.fire({
          title: `Editar ${config.titulo.replace('Gestionar ', '')}`,
          html: `<div style="display: flex; flex-direction: column; gap: 16px; text-align: left;">
            ${generarFormularioHTML(config, item)}
          </div>`,
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: "Guardar",
          cancelButtonText: "Cancelar",
          preConfirm: () => {
            const valores = obtenerValoresFormulario();
            const error = validarDatos(valores);
            if (error) {
              Swal.showValidationMessage(error);
              return false;
            }
            return valores;
          }
        });

        if (editValues) {
          try {
            const nit = localStorage.getItem("CodigoCliente");
            const ruta = `coordi_ph/${nit}/censo/${filaActual.id}`;
            const itemOriginal = datosModales[index];

            await guardarRegistroEnFirebase(tipo, editValues, ruta, itemOriginal.index);
            await sincronizarTodo();

            await Swal.fire({
              icon: "success",
              title: "Éxito",
              text: "Registro editado correctamente",
              timer: 1500,
              showConfirmButton: false
            });
            window.location.reload();
          } catch (error) {
            console.error("Error editando:", error);
            Swal.fire("Error", `No se pudo editar: ${error.message}`, "error");
          }
        }
      };

      const eliminarItem = async (index) => {
        const itemAEliminar = datosModales[index];
        
        const confirmacion = await Swal.fire({
          title: "¿Limpiar registro?",
          text: "Esta acción limpiará los datos del registro",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, limpiar",
          cancelButtonText: "Cancelar",
        });

        if (confirmacion.isConfirmed) {
          try {
            const nit = localStorage.getItem("CodigoCliente");
            const ruta = `coordi_ph/${nit}/censo/${filaActual.id}`;
            const itemOriginal = datosModales[index];

            await limpiarRegistroEnFirebase(tipo, ruta, itemOriginal.index);

            if (tipo === 'vehiculos' && itemAEliminar.placa) {
              const apartamento = filaActual.direccion || 'Sin dirección';
              enviarPlacaAPI(apartamento, itemAEliminar.placa).catch(console.error);
            }

            await sincronizarTodo();

            await Swal.fire({
              icon: "success",
              title: "Limpiado",
              text: "Registro limpiado correctamente",
              timer: 1500,
              showConfirmButton: false
            });
            window.location.reload();
          } catch (error) {
            console.error("Error eliminando:", error);
            Swal.fire("Error", `No se pudo limpiar: ${error.message}`, "error");
          }
        }
      };

      
      const agregarItem = async () => {
        const valores = obtenerValoresFormulario();
        const error = validarDatos(valores);

        if (error) {
          Swal.fire("Error", error, "error");
          return;
        }

        try {
          const nit = localStorage.getItem("CodigoCliente");
          
          if (!nit) {
            throw new Error("No se encontró el código de cliente");
          }

          if (!filaActual.id) {
            throw new Error("ID de vivienda no válido");
          }

          const ruta = `coordi_ph/${nit}/censo/${filaActual.id}`;
          const siguienteNumero = obtenerSiguienteNumero(filaActual, configCampos.campoBase);

          console.log(`Agregando registro ${tipo} #${siguienteNumero}`);
          await guardarRegistroEnFirebase(tipo, valores, ruta, siguienteNumero);
          config.campos.forEach(campo => {
            const campoFirebase = `${campo}_${siguienteNumero}`;
            filaActual[campoFirebase] = valores[campo] || "";
          });
          datosModales = extraerDatosNumerados(filaActual, config.campos);
          actualizarTablaModal(datosModales);
          sincronizarTodo().catch(console.error);
          limpiarFormulario();

          await Swal.fire({
            icon: "success",
            title: "Éxito",
            text: "Registro agregado correctamente",
            timer: 1500,
            showConfirmButton: false
          });
          window.location.reload();
        } catch (error) {
          console.error("Error agregando registro:", error);
          Swal.fire("Error", `No se pudo agregar el registro: ${error.message}`, "error");
        }
      };
      await Swal.fire({
        title: `${config.titulo} - ${fila.direccion}`,
        html: `
          <div style="display: flex; gap: 20px; max-width: 1200px; margin: 0 auto;">
            <div id="contenedorTabla" style="flex: 2; min-height: 200px;">
              ${datosModales.length > 0 
                ? generarTablaConAcciones(datosModales, config)
                : '<p style="text-align: center; color: #666; padding: 40px;">No hay registros. Agrega uno nuevo usando el formulario →</p>'
              }
            </div>
            <div style="flex: 1; text-align: left;">
              <h4 style="margin: 0 0 16px 0; color: #333;">➕ Agregar Nuevo</h4>
              <div style="display: flex; flex-direction: column; gap: 16px;">
                ${generarFormularioHTML(config)}
                <button id="btnAgregar" style="background: #28a745; color: white; border: none; padding: 12px; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 15px; transition: all 0.3s;">
                  ➕ Agregar Registro
                </button>
              </div>
            </div>
          </div>
        `,
        didOpen: () => {
          if (tipo === 'vehiculos') {
            const placaInput = document.getElementById('placa');
            if (placaInput) {
              placaInput.addEventListener('input', (e) => {
                e.target.value = e.target.value.toUpperCase();
              });
            }
          }

          window.editarItem = editarItem;
          window.eliminarItem = eliminarItem;

          const btnAgregar = document.getElementById('btnAgregar');
          if (btnAgregar) {
            btnAgregar.addEventListener('click', agregarItem);
            
            btnAgregar.addEventListener('mouseenter', () => {
              btnAgregar.style.background = '#218838';
              btnAgregar.style.transform = 'scale(1.02)';
            });
            btnAgregar.addEventListener('mouseleave', () => {
              btnAgregar.style.background = '#28a745';
              btnAgregar.style.transform = 'scale(1)';
            });
          }
        },
        willClose: () => {
          modalAbierto = false;
        },
        focusConfirm: false,
        showConfirmButton: false,
        showDenyButton: true,
        denyButtonText: "Cerrar",
        width: 1200,
      });
    };
  }, [actualizarFilaEspecifica]);
  const gestionarResidentes = useGestionDatosIntegrado('residentes');
  const gestionarVehiculos = useGestionDatosIntegrado('vehiculos');
  const gestionarMascotas = useGestionDatosIntegrado('mascotas');
  const gestionarAutorizados = useGestionDatosIntegrado('autorizados');
  const gestionarEspacios = useGestionDatosIntegrado('espacios');
  const gestionarContactosEmergencia = useGestionDatosIntegrado('contactos_emergencia');
  const importarDatos = useImportacionDatos(datos, capturarDatos);
  const crearCopropietarioActualizado = useCallback(async () => {
    await crearCopropietario();
    await capturarDatos();
  }, [capturarDatos]);

  const editarCopropietarioActualizado = useCallback(async (fila) => {
    await editarCopropietario(fila);
    await capturarDatos();
  }, [capturarDatos]);

  const eliminarCopropietarioActualizado = useCallback(async (fila) => {
    await eliminarCopropietario(fila);
    await capturarDatos();
  }, [capturarDatos]);
  const botonesAccion = [
    { texto: "Residentes", icono: "bi-people", accion: gestionarResidentes },
    { texto: "Vehículos", icono: "bi-car-front", accion: gestionarVehiculos },
    { texto: "Mascotas", icono: "bi-heart", accion: gestionarMascotas },
    { texto: "Autorizados", icono: "bi-person-check", accion: gestionarAutorizados },
    { texto: "Contactos Emergencia", icono: "bi-telephone", accion: gestionarContactosEmergencia },
    { texto: "Espacios (Parq - Bodega - C.Ú)", icono: "bi-box", accion: gestionarEspacios },
    { texto: "Editar", icono: "bi-pencil", accion: editarCopropietarioActualizado },
    { texto: "Eliminar", icono: "bi-trash", accion: eliminarCopropietarioActualizado },
  ];

  useEffect(() => {
    capturarDatos();
  }, [capturarDatos]);

  if (cargando) {
    return <AnimacionCarga />;
  }

  return (
    <>
      <TablaGenerica
        datos={datos}
        titulos={columnas.map((c) => c.label.toUpperCase())}
        campos={columnas.map((c) => c.key)}
        elementosPorPagina={3000}
        columnaOrdenInicial={1}
        direccionOrdenInicial="asc"
        botones={botonesAccion}
        contenidoExtra={
          <>
            <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
            <button onClick={importarDatos} title="Importar datos"><i className="bi bi-upload" /></button>
            <CSVLink data={datos} headers={columnas} filename="listado_censo.csv" separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
            <button onClick={crearCopropietarioActualizado} title="Crear Copropietario"><i className="bi bi-plus-lg" /></button>
          </>
        }
      />
      <BotonSOS />
    </>
  );
});

export default ListadoCenso;